// Typed axios wrappers around /api/v1/admin/facilities/* and
// /api/v1/admin/bookings/*. Powers the Phase 7b admin UI.

import type {
  PublicFacility,
  PublicBooking,
  FacilityType,
  BookingStatus,
  CreateFacilityRequest,
  UpdateFacilityRequest,
} from '@residenone/shared';
import { api } from './api-client';

export type {
  PublicFacility,
  PublicBooking,
  FacilityType,
  BookingStatus,
  CreateFacilityRequest,
  UpdateFacilityRequest,
};

// ─── Facilities ────────────────────────────────────────────────────────────
export interface ListFacilitiesParams {
  limit?: number;
  cursor?: string;
  type?: FacilityType;
}

export async function listFacilities(
  params: ListFacilitiesParams,
): Promise<{ items: PublicFacility[]; nextCursor: string | null }> {
  const { data } = await api.get<{ items: PublicFacility[]; nextCursor: string | null }>(
    '/admin/facilities',
    { params },
  );
  return data;
}

export async function getFacility(id: string): Promise<PublicFacility> {
  // No GET /admin/facilities/:id endpoint yet; reuse resident endpoint which
  // returns the same shape (staff is a superset). Fall back to filtering the
  // admin list if that ever changes.
  const { data } = await api.get<{ facility: PublicFacility }>(`/facilities/${id}`);
  return data.facility;
}

export async function createFacility(input: CreateFacilityRequest): Promise<PublicFacility> {
  const { data } = await api.post<{ facility: PublicFacility }>('/admin/facilities', input);
  return data.facility;
}

export async function updateFacility(
  id: string,
  patch: UpdateFacilityRequest,
): Promise<PublicFacility> {
  const { data } = await api.patch<{ facility: PublicFacility }>(
    `/admin/facilities/${id}`,
    patch,
  );
  return data.facility;
}

// ─── Bookings ──────────────────────────────────────────────────────────────
export interface ListBookingsParams {
  limit?: number;
  cursor?: string;
  status?: BookingStatus;
  facility_id?: string;
  since?: string;       // ISO datetime
}

export async function listAdminBookings(
  params: ListBookingsParams,
): Promise<{ items: PublicBooking[]; nextCursor: string | null }> {
  const { data } = await api.get<{ items: PublicBooking[]; nextCursor: string | null }>(
    '/admin/bookings',
    { params },
  );
  return data;
}

export async function approveBooking(id: string): Promise<PublicBooking> {
  const { data } = await api.post<{ booking: PublicBooking }>(
    `/admin/bookings/${id}/approve`,
  );
  return data.booking;
}

export async function rejectBooking(id: string, reason: string): Promise<PublicBooking> {
  const { data } = await api.post<{ booking: PublicBooking }>(
    `/admin/bookings/${id}/reject`,
    { reason },
  );
  return data.booking;
}
